import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';
import PageTransition from '../components/PageTransition';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import { formatCurrency as fmt } from '../utils/currency';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', lavender: '#A78BFA',
  text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .fi-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 32px 20px; border-bottom: 1px solid ${C.border};
  }
  .fi-title { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .fi-subtitle { color: ${C.muted}; font-size: 0.72rem; margin-top: 4px; }
  .fi-body { padding: 24px 32px; }

  .fi-tabs {
    display: flex; gap: 4px; margin-bottom: 24px;
    background: rgba(255,255,255,0.04); padding: 4px; border-radius: 12px; width: fit-content;
  }
  .fi-tab {
    padding: 8px 18px; border-radius: 9px; border: none; cursor: pointer;
    font-size: 0.82rem; transition: all .15s; font-family: inherit; white-space: nowrap;
    font-weight: 500; color: ${C.muted}; background: transparent;
  }
  .fi-tab.active {
    background: ${C.surface}; color: ${C.text}; font-weight: 700;
    box-shadow: 0 1px 6px rgba(0,0,0,0.25);
  }

  .fi-row-right { display: flex; justify-content: flex-end; margin-bottom: 20px; }
  .fi-row-between { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
  .fi-select {
    padding: 8px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    font-family: inherit; transition: border-color .15s; width: 220px;
  }
  .fi-select:focus { border-color: ${C.teal}; }
  select.fi-select option { background: ${C.surface}; }
  .fi-select-sm { width: 140px; font-size: 0.78rem; }
  .fi-loading { padding: 60px; text-align: center; color: ${C.muted}; }

  /* KPI cards */
  .fi-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
  @media (max-width: 900px) { .fi-kpi-grid { grid-template-columns: 1fr 1fr; } }
  .fi-kpi {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 12px;
    padding: 16px 20px; text-align: center;
  }
  .fi-kpi-label { font-size: 0.65rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 6px; }
  .fi-kpi-value { font-size: 1.25rem; font-weight: 800; font-family: 'JetBrains Mono', monospace; }
  .fi-kpi-sub { font-size: 0.68rem; color: ${C.muted}; margin-top: 3px; }

  /* Card */
  .fi-card {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px;
    padding: 20px 24px; overflow: hidden;
  }
  .fi-card-title { font-size: 0.9rem; font-weight: 800; color: ${C.text}; margin-bottom: 16px; }
  .fi-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  @media (max-width: 800px) { .fi-2col { grid-template-columns: 1fr; } }

  /* P&L rows */
  .fi-pl-section { font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
  .fi-pl-row { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; font-size: 0.82rem; }
  .fi-pl-divider { border: none; border-top: 1px solid ${C.border}; margin: 10px 0; }
  .fi-pl-subtotal { font-size: 0.68rem; color: ${C.muted}; }
  .fi-mono { font-family: 'JetBrains Mono', monospace; font-weight: 700; }

  /* Cash flow */
  .fi-flow-banner {
    padding: 10px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 0.82rem; font-weight: 600;
  }
  .fi-flow-banner.positive { background: rgba(0,201,177,0.1); border: 1px solid rgba(0,201,177,0.25); color: ${C.teal}; }
  .fi-flow-banner.negative { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.25); color: ${C.coral}; }

  .fi-card-head {
    padding: 14px 20px; border-bottom: 1px solid ${C.border};
    display: flex; align-items: center; justify-content: space-between;
  }
  .fi-card-head-title { font-weight: 700; font-size: 0.85rem; color: ${C.text}; }
  .fi-table { width: 100%; border-collapse: collapse; }
  .fi-table th {
    padding: 9px 16px; background: rgba(255,255,255,0.03); border-bottom: 1px solid ${C.border};
    font-size: 0.68rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase;
    letter-spacing: .06em; text-align: left; white-space: nowrap;
  }
  .fi-table td {
    padding: 10px 16px; border-bottom: 1px solid rgba(42,46,69,0.5);
    font-size: 0.8rem; color: ${C.sub};
  }
  .fi-table tbody tr:last-child td { border-bottom: none; }
  .fi-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .fi-table td.right { text-align: right; }
  .fi-badge {
    font-size: 0.65rem; font-weight: 700; padding: 2px 8px; border-radius: 20px; white-space: nowrap;
  }
  .fi-empty-cell { padding: 40px 24px; text-align: center; color: ${C.muted}; font-size: 0.82rem; }

  /* VAT */
  .fi-vat-icon {
    width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem; font-weight: 800; background: linear-gradient(135deg, #1B3A6B, #2563EB);
    color: white;
  }
  .fi-vat-row { display: flex; justify-content: space-between; padding: 6px 0; }
  .fi-vat-label { font-size: 0.82rem; color: ${C.muted}; }
  .fi-vat-val { font-family: 'JetBrains Mono', monospace; font-weight: 700; color: ${C.text}; }
  .fi-vat-due {
    margin-top: 16px; padding: 10px 14px; border-radius: 8px;
    background: rgba(0,201,177,0.1); border: 1px solid rgba(0,201,177,0.25);
    font-size: 0.75rem; color: ${C.teal}; font-weight: 600;
  }
  .fi-vat-warn {
    margin-top: 12px; padding: 8px 12px; border-radius: 8px;
    background: rgba(240,180,41,0.1); font-size: 0.72rem; color: ${C.gold};
  }
  .fi-toggle-wrap { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; cursor: pointer; }
  .fi-toggle { position: relative; width: 44px; height: 24px; border-radius: 12px; transition: background 200ms; cursor: pointer; flex-shrink: 0; }
  .fi-toggle-knob { position: absolute; top: 3px; width: 18px; height: 18px; border-radius: 50%; background: white; transition: left 200ms; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
  .fi-toggle-label { font-weight: 600; font-size: 0.85rem; color: ${C.text}; }
  .fi-price-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 4px; }
  .fi-price-opt { padding: 10px 12px; border-radius: 8px; cursor: pointer; text-align: left; transition: all .15s; }
  .fi-price-opt-title { font-weight: 700; font-size: 0.78rem; }
  .fi-price-opt-sub { font-size: 0.65rem; margin-top: 2px; }
  .fi-vat-hint {
    padding: 10px 14px; background: rgba(255,255,255,0.03); border-radius: 8px;
    font-size: 0.72rem; color: ${C.muted}; margin-bottom: 12px; line-height: 1.6;
  }
  .fi-save-btn {
    width: 100%; padding: 10px; border-radius: 8px; border: none; cursor: pointer;
    font-weight: 700; font-size: 0.85rem; transition: all 200ms;
  }

  /* Budgets */
  .fi-budget-bar-wrap { margin-bottom: 16px; }
  .fi-budget-bar-header { display: flex; justify-content: space-between; margin-bottom: 4px; }
  .fi-budget-bar-cat { font-weight: 600; font-size: 0.82rem; color: ${C.text}; }
  .fi-budget-bar-amounts { font-size: 0.72rem; color: ${C.muted}; }
  .fi-budget-bar-track { height: 8px; border-radius: 4px; background: rgba(255,255,255,0.08); overflow: hidden; }
  .fi-budget-bar-fill { height: 100%; border-radius: 4px; transition: width 600ms ease; }
  .fi-budget-bar-status { font-size: 0.65rem; font-weight: 600; margin-top: 3px; }

  .fi-empty {
    display: flex; flex-direction: column; align-items: center; padding: 56px 24px; gap: 8px; text-align: center;
  }
  .fi-empty-icon { font-size: 2rem; }
  .fi-empty-text { color: ${C.muted}; font-size: 0.85rem; margin-bottom: 4px; }

  /* Buttons */
  .fi-btn-primary {
    padding: 9px 16px; background: ${C.gold}; color: #0B0E17; border: none; border-radius: 9px;
    font-size: 0.82rem; font-weight: 700; cursor: pointer; transition: opacity .15s; font-family: inherit;
  }
  .fi-btn-primary:hover { opacity: .88; }
  .fi-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
  .fi-btn-outline {
    padding: 9px 16px; background: transparent; border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.sub}; font-size: 0.82rem; font-weight: 600;
    cursor: pointer; transition: all .15s; font-family: inherit;
  }
  .fi-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .fi-btn-outline:disabled { opacity: .45; cursor: not-allowed; }

  /* Modal */
  .fi-overlay {
    position: fixed; inset: 0; background: rgba(7,9,14,0.75);
    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center;
    padding: 20px; z-index: 1000;
  }
  .fi-modal {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px;
    width: 100%; max-height: 90vh; overflow-y: auto; animation: fi-enter .18s ease;
  }
  @keyframes fi-enter { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  .fi-modal-head {
    display: flex; justify-content: space-between; align-items: center;
    padding: 18px 22px 16px; border-bottom: 1px solid ${C.border};
  }
  .fi-modal-title { font-size: 1rem; font-weight: 800; color: ${C.text}; }
  .fi-modal-close {
    padding: 6px 12px; background: transparent; border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.muted}; font-size: 0.78rem; cursor: pointer; transition: all .15s;
  }
  .fi-modal-close:hover { border-color: ${C.coral}; color: ${C.coral}; }
  .fi-modal-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
  .fi-modal-foot {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 22px; border-top: 1px solid ${C.border};
  }
  .fi-form-group { display: flex; flex-direction: column; gap: 6px; }
  .fi-label { font-size: 0.73rem; font-weight: 600; color: ${C.muted}; }
  .fi-input {
    padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    transition: border-color .15s; width: 100%; box-sizing: border-box; font-family: inherit;
  }
  .fi-input:focus { border-color: ${C.teal}; }
  select.fi-input option { background: ${C.surface}; }
  .fi-modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; padding: 18px 22px; }
  @media (max-width: 480px) { .fi-modal-grid { grid-template-columns: 1fr; } }
  .fi-modal-hint { font-size: 0.75rem; color: ${C.muted}; }
`;

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const BUDGET_CATEGORIES = ['Revenue','Rent','Salaries','Utilities','Transport','Marketing','Stock/Purchases','Other'];

function periodOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({ month: d.getMonth() + 1, year: d.getFullYear(),
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }) });
  }
  return options;
}

function pct(n) { return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`; }

function KpiCard({ label, value, color = C.text, sub }) {
  const anim = useAnimatedCounter(value, 1200);
  return (
    <div className="fi-kpi">
      <div className="fi-kpi-label">{label}</div>
      <div className="fi-kpi-value" style={{ color }}>{fmt(anim)}</div>
      {sub && <div className="fi-kpi-sub">{sub}</div>}
    </div>
  );
}

function ComparisonChart({ data }) {
  if (!data?.length) return <div style={{ color: C.muted, padding: 40, textAlign: 'center', fontSize: '0.82rem' }}>No comparison data</div>;
  const maxVal = Math.max(...data.map((d) => Math.max(d.revenue, Math.max(0, d.netProfit))), 1);
  const W = 520; const H = 200; const BAR = 18; const GAP = 4; const GUTTER = 40;
  const slotW = (W - GUTTER) / data.length;
  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} style={{ width: '100%', maxWidth: W }}>
      {data.map((d, i) => {
        const x = GUTTER + i * slotW;
        const revH  = (d.revenue / maxVal) * H;
        const profH = Math.max(0, (d.netProfit / maxVal) * H);
        return (
          <g key={i}>
            <rect x={x + 2}             y={H - revH}  width={BAR} height={revH}  fill={C.sky} rx={3} opacity={0.75} />
            <rect x={x + 2 + BAR + GAP} y={H - profH} width={BAR} height={profH} fill={C.gold} rx={3} opacity={0.85} />
            <text x={x + BAR}  y={H + 18} textAnchor="middle" fontSize={9} fill={C.muted}>{d.label}</text>
          </g>
        );
      })}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t} x1={GUTTER} x2={W} y1={H - t * H} y2={H - t * H} stroke={C.border} strokeWidth={0.5} />
      ))}
      <rect x={GUTTER}      y={H + 22} width={10} height={8} fill={C.sky}  rx={2} />
      <text x={GUTTER + 14} y={H + 30} fontSize={8} fill={C.muted}>Revenue</text>
      <rect x={GUTTER + 70} y={H + 22} width={10} height={8} fill={C.gold} rx={2} />
      <text x={GUTTER + 84} y={H + 30} fontSize={8} fill={C.muted}>Net Profit</text>
    </svg>
  );
}

function DailyFlowChart({ dailyFlow }) {
  if (!dailyFlow?.length) return null;
  const maxVal = Math.max(...dailyFlow.map((d) => Math.max(d.in, d.out)), 1);
  const W = 520; const H = 140; const GUTTER = 8;
  const barW = Math.max(2, (W - GUTTER) / dailyFlow.length - 1);
  return (
    <svg viewBox={`0 0 ${W} ${H + 20}`} style={{ width: '100%' }}>
      {dailyFlow.map((d, i) => {
        const x     = GUTTER + i * ((W - GUTTER) / dailyFlow.length);
        const inH   = (d.in  / maxVal) * H;
        const outH  = (d.out / maxVal) * H;
        return (
          <g key={i}>
            {d.in  > 0 && <rect x={x}          y={H - inH}  width={barW / 2} height={inH}  fill={C.teal} rx={1} opacity={0.75} />}
            {d.out > 0 && <rect x={x + barW/2}  y={H - outH} width={barW / 2} height={outH} fill={C.coral} rx={1} opacity={0.7} />}
          </g>
        );
      })}
      <line x1={GUTTER} x2={W} y1={H} y2={H} stroke={C.border} />
      <rect x={GUTTER}      y={H + 6} width={8} height={6} fill={C.teal}  rx={1} />
      <text x={GUTTER + 12} y={H + 13} fontSize={8} fill={C.muted}>Cash In</text>
      <rect x={GUTTER + 60} y={H + 6} width={8} height={6} fill={C.coral} rx={1} />
      <text x={GUTTER + 74} y={H + 13} fontSize={8} fill={C.muted}>Cash Out</text>
    </svg>
  );
}

const plRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', fontSize: '0.82rem' };

function PLTab() {
  const periods = periodOptions();
  const [selIdx, setSelIdx] = useState(0);
  const [pl, setPL]         = useState(null);
  const [comparison, setComparison] = useState([]);
  const [loading, setLoading] = useState(true);

  const { month, year } = periods[selIdx];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plRes, compRes] = await Promise.all([
        api.get(`/financials/pl?month=${month}&year=${year}`),
        api.get('/financials/pl/comparison?months=6'),
      ]);
      setPL(plRes.data);
      setComparison(compRes.data);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const marginColor = (m) => m >= 20 ? C.teal : m >= 5 ? C.gold : C.coral;

  return (
    <div>
      <div className="fi-row-right">
        <select className="fi-select" value={selIdx} onChange={(e) => setSelIdx(Number(e.target.value))}>
          {periods.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
        </select>
      </div>

      {loading || !pl ? (
        <div className="fi-loading">Loading P&L...</div>
      ) : (
        <>
          <div className="fi-kpi-grid">
            <KpiCard label="Total Revenue"  value={pl.revenue}       color={C.teal}   sub={pl.period.label} />
            <KpiCard label="Gross Profit"   value={pl.grossProfit}   color={C.sky}    sub={`Margin: ${pl.grossMargin.toFixed(1)}%`} />
            <KpiCard label="Total Expenses" value={pl.totalExpenses} color={C.coral} />
            <KpiCard label="Net Profit"     value={pl.netProfit}     color={pl.netProfit >= 0 ? C.gold : C.coral} sub={`Margin: ${pl.netMargin.toFixed(1)}%`} />
          </div>

          <div className="fi-2col">
            <div className="fi-card">
              <div className="fi-card-title">Income Statement — {pl.period.label}</div>

              <div className="fi-pl-section">Revenue</div>
              <div style={plRow}>
                <span style={{ color: C.sub }}>Product Sales</span>
                <span className="fi-mono" style={{ color: C.teal }}>{fmt(pl.revenue)}</span>
              </div>
              <div style={plRow}>
                <span style={{ color: C.sub }}>Cost of Goods Sold</span>
                <span className="fi-mono" style={{ color: C.coral }}>− {fmt(pl.cogs)}</span>
              </div>
              <hr className="fi-pl-divider" />
              <div style={{ ...plRow, marginBottom: 16 }}>
                <span style={{ fontWeight: 700, color: C.text }}>GROSS PROFIT</span>
                <div style={{ textAlign: 'right' }}>
                  <div className="fi-mono" style={{ color: C.sky, fontSize: '1rem' }}>{fmt(pl.grossProfit)}</div>
                  <div style={{ fontSize: '0.65rem', color: marginColor(pl.grossMargin) }}>Margin {pl.grossMargin.toFixed(1)}%</div>
                </div>
              </div>

              <div className="fi-pl-section">Operating Expenses</div>
              {Object.keys(pl.expenses).length === 0 ? (
                <div style={{ fontSize: '0.78rem', color: C.muted, padding: '4px 0', marginBottom: 8 }}>No expenses recorded</div>
              ) : (
                Object.entries(pl.expenses).map(([cat, amt]) => (
                  <div key={cat} style={plRow}>
                    <span style={{ color: C.sub }}>{cat}</span>
                    <span className="fi-mono" style={{ color: C.coral }}>− {fmt(amt)}</span>
                  </div>
                ))
              )}
              <hr className="fi-pl-divider" />
              <div style={plRow}>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: pl.netProfit >= 0 ? C.gold : C.coral }}>NET PROFIT</span>
                <div style={{ textAlign: 'right' }}>
                  <div className="fi-mono" style={{ fontSize: '1.1rem', color: pl.netProfit >= 0 ? C.gold : C.coral }}>{fmt(pl.netProfit)}</div>
                  <div style={{ fontSize: '0.65rem', color: marginColor(pl.netMargin) }}>Margin {pl.netMargin.toFixed(1)}%</div>
                </div>
              </div>
            </div>

            <div className="fi-card">
              <div className="fi-card-title">6-Month Trend</div>
              <ComparisonChart data={comparison} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function VATTab() {
  const periods = periodOptions();
  const [selIdx, setSelIdx] = useState(0);
  const [summary, setSummary]   = useState(null);
  const [form, setForm] = useState({ vatEnabled: false, vatRate: 15, vatNumber: '', vatInclusive: true });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);

  const { month, year } = periods[selIdx];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sumRes, settRes] = await Promise.all([
          api.get(`/financials/vat/summary?month=${month}&year=${year}`),
          api.get('/financials/vat/settings'),
        ]);
        setSummary(sumRes.data);
        setForm({
          vatEnabled:   settRes.data.vatEnabled,
          vatRate:      Number(settRes.data.vatRate),
          vatNumber:    settRes.data.vatNumber || '',
          vatInclusive: settRes.data.vatInclusive,
        });
      } catch {}
      setLoading(false);
    };
    load();
  }, [month, year]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/financials/vat/settings', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      const sumRes = await api.get(`/financials/vat/summary?month=${month}&year=${year}`);
      setSummary(sumRes.data);
    } catch {}
    setSaving(false);
  };

  if (loading) return <div className="fi-loading">Loading VAT data...</div>;

  const dueDateLabel = summary?.dueDate
    ? new Date(summary.dueDate).toLocaleDateString('en-GH', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const vatRows = [
    { label: 'Total Sales (incl. VAT)',         val: summary?.totalSales,           color: C.sub },
    { label: 'VAT-Exclusive Revenue',           val: summary?.vatExclusiveRevenue,  color: C.sub },
    { label: 'Output VAT Collected',            val: summary?.vatCollected,         color: C.coral, bold: true },
    null,
    { label: 'VAT on Purchases (Input)',        val: summary?.vatOnPurchases,       color: C.teal },
    null,
    { label: 'VAT PAYABLE TO GRA',              val: summary?.vatPayable,           color: C.gold, bold: true, large: true },
  ];

  return (
    <div>
      <div className="fi-row-right">
        <select className="fi-select" value={selIdx} onChange={(e) => setSelIdx(Number(e.target.value))}>
          {periods.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
        </select>
      </div>

      <div className="fi-2col">
        <div className="fi-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div className="fi-vat-icon">{form.vatRate}%</div>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>Ghana VAT Summary</div>
              <div style={{ fontSize: '0.72rem', color: C.muted }}>{periods[selIdx].label}</div>
            </div>
          </div>

          {vatRows.map((row, i) => {
            if (!row) return <hr key={i} className="fi-pl-divider" />;
            return (
              <div key={i} className="fi-vat-row" style={{ fontSize: row.large ? '1rem' : '0.82rem' }}>
                <span className="fi-vat-label">{row.label}</span>
                <span className="fi-mono" style={{ fontWeight: row.bold ? 800 : 600, color: row.color }}>{fmt(row.val ?? 0)}</span>
              </div>
            );
          })}

          {dueDateLabel && <div className="fi-vat-due">✓ VAT return due by {dueDateLabel}</div>}
          {!form.vatEnabled && <div className="fi-vat-warn">Enable VAT in settings to see your VAT obligation</div>}
        </div>

        <div className="fi-card">
          <div className="fi-card-title">VAT Settings</div>

          <div className="fi-toggle-wrap" onClick={() => setForm((f) => ({ ...f, vatEnabled: !f.vatEnabled }))}>
            <div className="fi-toggle" style={{ background: form.vatEnabled ? C.teal : C.border }}>
              <div className="fi-toggle-knob" style={{ left: form.vatEnabled ? 23 : 3 }} />
            </div>
            <span className="fi-toggle-label">VAT Registered Business</span>
          </div>

          {form.vatEnabled && (
            <>
              <div className="fi-form-group" style={{ marginBottom: 12 }}>
                <label className="fi-label">VAT Number (TIN)</label>
                <input className="fi-input" value={form.vatNumber} onChange={(e) => setForm((f) => ({ ...f, vatNumber: e.target.value }))} placeholder="e.g. GHA-123456789" />
              </div>
              <div className="fi-form-group" style={{ marginBottom: 12 }}>
                <label className="fi-label">VAT Rate (%)</label>
                <input className="fi-input" type="number" min={0} max={50} value={form.vatRate} onChange={(e) => setForm((f) => ({ ...f, vatRate: Number(e.target.value) }))} />
              </div>
              <div className="fi-form-group" style={{ marginBottom: 12 }}>
                <label className="fi-label">Price Mode</label>
                <div className="fi-price-grid">
                  {[{ val: true, label: 'VAT Inclusive', sub: 'Prices already include VAT' }, { val: false, label: 'VAT Exclusive', sub: 'VAT added on top' }].map((opt) => (
                    <button key={String(opt.val)} onClick={() => setForm((f) => ({ ...f, vatInclusive: opt.val }))}
                      className="fi-price-opt"
                      style={{
                        border: `2px solid ${form.vatInclusive === opt.val ? C.teal : C.border}`,
                        background: form.vatInclusive === opt.val ? 'rgba(0,201,177,0.08)' : 'transparent',
                      }}>
                      <div className="fi-price-opt-title" style={{ color: form.vatInclusive === opt.val ? C.teal : C.text }}>{opt.label}</div>
                      <div className="fi-price-opt-sub" style={{ color: C.muted }}>{opt.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="fi-vat-hint">
                <strong>Inclusive:</strong> GHS 115 price = GHS 100 + GHS 15 VAT<br />
                <strong>Exclusive:</strong> GHS 100 price + 15% = GHS 115 at checkout
              </div>
            </>
          )}

          <button onClick={handleSave} disabled={saving} className="fi-save-btn"
            style={{ background: saved ? C.teal : C.gold, color: '#0B0E17' }}>
            {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save VAT Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CashFlowTab() {
  const periods = periodOptions();
  const [selIdx, setSelIdx]   = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [openingForm, setOpeningForm] = useState({ show: false, value: '' });
  const [savingOpening, setSavingOpening] = useState(false);

  const { month, year } = periods[selIdx];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/financials/cashflow?month=${month}&year=${year}`);
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const handleSetOpening = async () => {
    if (!openingForm.value) return;
    setSavingOpening(true);
    try {
      await api.post('/financials/cashflow/opening', { month, year, balance: Number(openingForm.value) });
      setOpeningForm({ show: false, value: '' });
      load();
    } catch {}
    setSavingOpening(false);
  };

  const TYPE_META = {
    sales:   { label: 'Sales',   bg: 'rgba(0,201,177,0.12)',   color: C.teal  },
    expense: { label: 'Expense', bg: 'rgba(255,107,107,0.12)', color: C.coral },
    opening: { label: 'Opening', bg: 'rgba(56,189,248,0.12)',  color: C.sky   },
    other:   { label: 'Other',   bg: 'rgba(136,146,164,0.1)',  color: C.muted },
  };

  const filteredEntries = (data?.entries || []).filter((e) => typeFilter === 'all' || e.type === typeFilter);
  const positive = data ? data.closingBalance >= data.openingBalance : false;

  return (
    <div>
      <div className="fi-row-between">
        <select className="fi-select" value={selIdx} onChange={(e) => setSelIdx(Number(e.target.value))}>
          {periods.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
        </select>
        <button className="fi-btn-outline" onClick={() => setOpeningForm({ show: true, value: data?.openingBalance ?? '' })}>
          Set Opening Balance
        </button>
      </div>

      {loading ? (
        <div className="fi-loading">Loading cash flow...</div>
      ) : data && (
        <>
          <div className="fi-kpi-grid">
            <KpiCard label="Opening Balance" value={data.openingBalance} color={C.sub} />
            <KpiCard label="Cash In"         value={data.cashIn}         color={C.teal} />
            <KpiCard label="Cash Out"        value={data.cashOut}        color={C.coral} />
            <KpiCard label="Closing Balance" value={data.closingBalance} color={data.closingBalance >= 0 ? C.gold : C.coral} />
          </div>

          {data.entries.length > 0 && (
            <div className={`fi-flow-banner ${positive ? 'positive' : 'negative'}`}>
              {positive ? '✓ Positive cash flow this month' : '⚠ Negative cash flow — review your expenses'}
            </div>
          )}

          {data.dailyFlow.some((d) => d.in > 0 || d.out > 0) && (
            <div className="fi-card" style={{ marginBottom: 20 }}>
              <div className="fi-card-title">Daily Cash Flow</div>
              <DailyFlowChart dailyFlow={data.dailyFlow} />
            </div>
          )}

          <div className="fi-card" style={{ padding: 0 }}>
            <div className="fi-card-head">
              <div className="fi-card-head-title">Cash Flow Entries</div>
              <select className="fi-select fi-select-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">All Types</option>
                <option value="sales">Sales</option>
                <option value="expense">Expense</option>
                <option value="opening">Opening</option>
              </select>
            </div>
            {filteredEntries.length === 0 ? (
              <div className="fi-empty-cell">
                {data.entries.length === 0 ? 'Cash flow is calculated from recorded sales and expenses. Set your opening balance to get started.' : 'No entries for this filter.'}
              </div>
            ) : (
              <table className="fi-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Cash In</th>
                    <th style={{ textAlign: 'right' }}>Cash Out</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((e, i) => {
                    const meta = TYPE_META[e.type] || TYPE_META.other;
                    return (
                      <tr key={i}>
                        <td style={{ fontSize: '0.75rem' }}>
                          {new Date(e.date).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' })}
                        </td>
                        <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</td>
                        <td>
                          <span className="fi-badge" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                        </td>
                        <td className="right fi-mono" style={{ color: C.teal, fontSize: '0.78rem' }}>{e.cashIn > 0 ? fmt(e.cashIn) : '—'}</td>
                        <td className="right fi-mono" style={{ color: C.coral, fontSize: '0.78rem' }}>{e.cashOut > 0 ? fmt(e.cashOut) : '—'}</td>
                        <td className="right fi-mono" style={{ color: e.balance >= 0 ? C.sky : C.coral, fontSize: '0.78rem' }}>{fmt(e.balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {openingForm.show && (
        <div className="fi-overlay" onClick={(e) => e.target === e.currentTarget && setOpeningForm({ show: false, value: '' })}>
          <div className="fi-modal" style={{ maxWidth: 380 }}>
            <div className="fi-modal-head">
              <div className="fi-modal-title">Set Opening Balance</div>
              <button className="fi-modal-close" onClick={() => setOpeningForm({ show: false, value: '' })}>✕</button>
            </div>
            <div className="fi-modal-body">
              <p className="fi-modal-hint">
                This is your cash-on-hand at the start of {periods[selIdx].label}. Setting this helps calculate your true cash position.
              </p>
              <div className="fi-form-group">
                <label className="fi-label">Opening Balance (GHS)</label>
                <input className="fi-input" type="number" min={0} value={openingForm.value}
                  onChange={(e) => setOpeningForm((f) => ({ ...f, value: e.target.value }))} autoFocus />
              </div>
            </div>
            <div className="fi-modal-foot">
              <button className="fi-btn-outline" onClick={() => setOpeningForm({ show: false, value: '' })}>Cancel</button>
              <button className="fi-btn-primary" onClick={handleSetOpening} disabled={savingOpening}>
                {savingOpening ? 'Saving...' : 'Set Balance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetsTab() {
  const periods = periodOptions();
  const [selIdx, setSelIdx]   = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [budgetForm, setBudgetForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);

  const { month, year } = periods[selIdx];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/financials/budgets?month=${month}&year=${year}`);
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    const initial = {};
    BUDGET_CATEGORIES.forEach((cat) => {
      const existing = data?.budgets?.find((b) => b.category === cat);
      initial[cat] = existing ? String(existing.amount) : '';
    });
    setBudgetForm(initial);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = BUDGET_CATEGORIES
        .filter((cat) => budgetForm[cat] !== '' && Number(budgetForm[cat]) >= 0)
        .map((cat) => api.post('/financials/budgets', { month, year, category: cat, amount: Number(budgetForm[cat]) }));
      await Promise.all(promises);
      setShowModal(false);
      load();
    } catch {}
    setSaving(false);
  };

  const copyLastMonth = async () => {
    setCopyLoading(true);
    try {
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear  = month === 1 ? year - 1 : year;
      const res = await api.get(`/financials/budgets?month=${prevMonth}&year=${prevYear}`);
      const prevBudgets = res.data.budgets || [];
      if (prevBudgets.length === 0) { alert('No budgets found for last month.'); setCopyLoading(false); return; }
      const promises = prevBudgets.map((b) =>
        api.post('/financials/budgets', { month, year, category: b.category, amount: b.amount })
      );
      await Promise.all(promises);
      load();
    } catch {}
    setCopyLoading(false);
  };

  const barColor = (status, cat) => {
    if (cat === 'Revenue') return status === 'on-track' ? C.teal : C.sky;
    if (status === 'over') return C.coral;
    if (status === 'on-track') return C.gold;
    return C.teal;
  };

  const statusColor = (status, cat) => {
    if (cat === 'Revenue') return status === 'on-track' ? C.teal : C.coral;
    if (status === 'over') return C.coral;
    if (status === 'on-track') return C.gold;
    return C.teal;
  };

  return (
    <div>
      <div className="fi-row-between">
        <select className="fi-select" value={selIdx} onChange={(e) => setSelIdx(Number(e.target.value))}>
          {periods.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="fi-btn-outline" onClick={copyLastMonth} disabled={copyLoading}>
            {copyLoading ? 'Copying...' : 'Copy Last Month'}
          </button>
          <button className="fi-btn-primary" onClick={openModal}>+ Set Budgets</button>
        </div>
      </div>

      {loading ? (
        <div className="fi-loading">Loading budgets...</div>
      ) : !data || data.budgets.length === 0 ? (
        <div className="fi-empty">
          <div className="fi-empty-icon">📊</div>
          <div className="fi-empty-text">No budgets set for {periods[selIdx].label}</div>
          <button className="fi-btn-primary" onClick={openModal}>+ Set Your First Budget</button>
        </div>
      ) : (
        <div className="fi-2col">
          <div className="fi-card">
            <div className="fi-card-title">Budget Progress</div>
            {data.budgets.map((b) => {
              const pctUsed = b.amount > 0 ? Math.min((b.actualAmount / b.amount) * 100, 100) : 0;
              const overage = b.actualAmount > b.amount;
              const sc = statusColor(b.status, b.category);
              const bc = barColor(b.status, b.category);
              return (
                <div key={b.category} className="fi-budget-bar-wrap">
                  <div className="fi-budget-bar-header">
                    <span className="fi-budget-bar-cat">{b.category}</span>
                    <span className="fi-budget-bar-amounts">{fmt(b.actualAmount)} / {fmt(b.amount)}</span>
                  </div>
                  <div className="fi-budget-bar-track">
                    <div className="fi-budget-bar-fill" style={{ width: `${pctUsed}%`, background: bc }} />
                  </div>
                  <div className="fi-budget-bar-status" style={{ color: sc }}>
                    {overage ? `${fmt(b.actualAmount - b.amount)} over budget` : b.status === 'on-track' ? 'Nearly at limit' : 'On track'}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="fi-card" style={{ padding: 0 }}>
            <div className="fi-card-head">
              <div className="fi-card-head-title">Budget vs Actual</div>
              <div style={{ fontSize: '0.7rem', color: C.muted }}>{periods[selIdx].label}</div>
            </div>
            <table className="fi-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ textAlign: 'right' }}>Budget</th>
                  <th style={{ textAlign: 'right' }}>Actual</th>
                  <th style={{ textAlign: 'right' }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {data.budgets.map((b) => {
                  const over = b.category !== 'Revenue' ? b.actualAmount > b.amount : b.actualAmount < b.amount;
                  return (
                    <tr key={b.category}>
                      <td style={{ fontWeight: 600, color: C.text }}>{b.category}</td>
                      <td className="right fi-mono" style={{ fontSize: '0.78rem' }}>{fmt(b.amount)}</td>
                      <td className="right fi-mono" style={{ fontSize: '0.78rem', fontWeight: 700 }}>{fmt(b.actualAmount)}</td>
                      <td className="right">
                        <span className="fi-badge" style={{
                          background: over ? 'rgba(255,107,107,0.12)' : 'rgba(0,201,177,0.12)',
                          color: over ? C.coral : C.teal,
                        }}>
                          {over ? '+' : ''}{pct(b.variancePct)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {data.revenueBudget > 0 && (
                  <tr style={{ background: 'rgba(255,255,255,0.025)', borderTop: `2px solid ${C.border}` }}>
                    <td style={{ fontWeight: 700, color: C.gold }}>Net Profit</td>
                    <td className="right fi-mono" style={{ fontWeight: 700 }}>{fmt(data.revenueBudget - data.summary.totalBudgeted)}</td>
                    <td className="right fi-mono" style={{ fontWeight: 700, color: data.revenueActual - data.summary.totalActual >= 0 ? C.teal : C.coral }}>
                      {fmt(data.revenueActual - data.summary.totalActual)}
                    </td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fi-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="fi-modal" style={{ maxWidth: 520 }}>
            <div className="fi-modal-head">
              <div className="fi-modal-title">Set Budgets — {periods[selIdx].label}</div>
              <button className="fi-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="fi-modal-grid">
              {BUDGET_CATEGORIES.map((cat) => (
                <div key={cat} className="fi-form-group">
                  <label className="fi-label">{cat} {cat === 'Revenue' ? '(Target)' : ''}</label>
                  <input className="fi-input" type="number" min={0} placeholder="GHS 0.00"
                    value={budgetForm[cat] || ''}
                    onChange={(e) => setBudgetForm((f) => ({ ...f, [cat]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="fi-modal-foot">
              <button className="fi-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="fi-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Budgets'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: 'pl',       label: 'P&L Statement' },
  { key: 'vat',      label: 'VAT Calculator' },
  { key: 'cashflow', label: 'Cash Flow' },
  { key: 'budgets',  label: 'Budgets' },
];

export default function FinancialIntelligence() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'pl';
  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });

  return (
    <Layout>
      <style>{STYLES}</style>
      <PageTransition>
        <div className="fi-header">
          <div>
            <h2 className="fi-title">Financial Intelligence</h2>
            <div className="fi-subtitle">P&L · VAT · Cash Flow · Budgets</div>
          </div>
        </div>

        <div className="fi-body">
          <div className="fi-tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`fi-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'pl'       && <PLTab />}
          {tab === 'vat'      && <VATTab />}
          {tab === 'cashflow' && <CashFlowTab />}
          {tab === 'budgets'  && <BudgetsTab />}
        </div>
      </PageTransition>
    </Layout>
  );
}
